import { useState } from "react";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";
import ApiRoundedIcon from "@mui/icons-material/ApiRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography
} from "@mui/material";

interface ScaffoldResource {
  id: string;
  title: string;
  description: string;
  icon: JSX.Element;
  steps: string[];
  snippet?: string;
  language?: string;
}

function CopySnippetButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error("Clipboard copy failed", error);
    }
  };

  return (
    <Button
      variant="text"
      size="small"
      startIcon={<ContentCopyRoundedIcon fontSize="small" />}
      onClick={handleCopy}
      sx={{ alignSelf: "flex-end" }}
    >
      {copied ? "Copiato" : "Copia"}
    </Button>
  );
}

const scaffoldResources: ScaffoldResource[] = [
  {
    id: "frontend-page",
    title: "Nuova pagina React",
    description:
      "Boilerplate di partenza per creare una vista nella console. Associa layout responsive, stato locale e tipografia di base.",
    icon: <BuildRoundedIcon color="primary" />,
    steps: [
      "Crea un nuovo file in src/pages/ e incolla il template riportato.",
      "Aggiungi la voce di menu e la route in AppLayout.tsx.",
      "Registra eventuali hook/API necessari in src/api/hooks.ts.",
      "Verifica la pagina con npm run dev e aggiorna i test Playwright se necessari."
    ],
    snippet: `import { Stack, Typography, Paper } from "@mui/material";

export function FeatureScaffold() {
  return (
    <Stack spacing={3}>
      <Typography variant="h4" gutterBottom>
        Nuova funzionalità
      </Typography>
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Inizia da qui con componenti MUI e hook personalizzati.
        </Typography>
      </Paper>
    </Stack>
  );
}
`
  },
  {
    id: "api-usage",
    title: "API REST – sintassi e pattern",
    description:
      "Promemoria rapido per integrare le API (auth, paginazione, filtri, download). Utile come riferimento in UI e per client esterni.",
    icon: <ApiRoundedIcon color="primary" />,
    steps: [
      "Autenticazione: header Authorization: Bearer <token> (token locale o API token con scope/permessi).",
      "Paginazione: limit/offset con total in risposta. Date in formato YYYY-MM-DD, intervalli con date_from/date_to.",
      "Path sensibili: folder/filename sono in path, usa encodeURIComponent. Gli allegati sono indicizzati da id (1-based).",
      "Errori: forma {detail:{code,message}}; gestire 400/401/403/404/500.",
      "Audit: /api/v1/system/audit con filtri method/status/user/q. Allegati: /api/v1/accounts/{acc}/emails/{date}/{folder}/{file}/attachments/{id}."
    ],
    snippet: `# esempio download allegato (curl)
curl -L -H "Authorization: Bearer $TOKEN" \\
  -o allegato.bin \\
  "http://HOST:PORT/api/v1/accounts/account@example.com/emails/2025-12-29/INBOX/12345_subject.eml/attachments/1"

# esempio audit log filtrato
curl -s -H "Authorization: Bearer $TOKEN" \\
  "http://HOST:PORT/api/v1/system/audit?limit=25&method=GET&status=200"`
  },
  {
    id: "frontend-hook",
    title: "Hook React Query",
    description:
      "Struttura consigliata per interrogare le API con caching, retry e invalidazione automatici.",
    icon: <ScienceRoundedIcon color="primary" />,
    steps: [
      "Definisci il tipo della risposta nel file hooks.ts.",
      "Configura la chiave di cache e imposta retry/polling secondo necessità.",
      "Richiama il nuovo hook dalla pagina o dal componente interessato.",
      "Aggiungi eventuali mock negli e2e test se il backend non è disponibile."
    ],
    snippet: `import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../api/client";

export interface ExamplePayload {
  id: string;
  label: string;
}

export function useExampleResource() {
  return useQuery({
    queryKey: ["example", "list"],
    queryFn: async () => {
      const response = await apiClient.get<ExamplePayload[]>("/api/v1/example");
      return response.data;
    },
    staleTime: 60_000,
    retry: 1
  });
}
`
  },
  {
    id: "playwright",
    title: "Test end-to-end",
    description:
      "Skeleton per creare una suite Playwright dedicata alla nuova sezione, pronto per essere esteso con asserzioni specifiche.",
    icon: <DescriptionRoundedIcon color="primary" />,
    steps: [
      "Duplica un file esistente in tests-e2e/ e aggiorna titolo e selettori.",
      "Utilizza data-testid per agganciare gli elementi dinamici nella pagina.",
      "Esegui npm run test:e2e o npm run test:e2e:headed per la verifica locale.",
      "Allega screenshot o video nelle pull request in caso di regressioni."
    ],
    snippet: `import { test, expect } from "@playwright/test";

test.describe("Developer scaffolding", () => {
  test("mostra la pagina di scaffolding", async ({ page }) => {
    await page.goto("/ui/developers");
    await expect(page.getByRole("heading", { name: "Area sviluppatori" })).toBeVisible();
  });
});
`
  }
];

const developerChecklist = [
  "Lancia npm run lint prima di effettuare il commit.",
  "Esegui npm run build per assicurare che la compilazione passi.",
  "Aggiorna la documentazione (README e guida utente) quando introduci nuove funzionalità.",
  "Aggiungi test unitari o e2e a copertura delle modifiche.",
  "Sincronizza i tipi condivisi tra backend e frontend per evitare mismatch."
];

export function DeveloperScaffolding() {
  return (
    <Stack spacing={4}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Area sviluppatori
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Raccolta di template e linee guida per estendere rapidamente la console. Utilizza questa sezione per
          mantenere coerenza tra viste, API client e suite di test.
        </Typography>
      </Box>

      <Alert severity="info">
        Ogni scaffolding fornisce un punto di partenza: personalizzalo in base alle esigenze della funzionalità e
        ricordati di allineare le modifiche con il team prima del rilascio.
      </Alert>

      <Card>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Checklist di qualità
            </Typography>
            <Chip label="Best practice" color="primary" variant="outlined" />
          </Stack>
          <Divider sx={{ my: 2 }} />
          <List dense>
            {developerChecklist.map((item) => (
              <ListItem key={item}>
                <ListItemIcon>
                  <TerminalRoundedIcon color="action" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {scaffoldResources.map((resource) => (
          <Grid item xs={12} md={4} key={resource.id}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  {resource.icon}
                  <Typography variant="h6">{resource.title}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {resource.description}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {resource.steps.map((step, index) => (
                    <ListItem key={`${resource.id}-step-${index}`}>
                      <ListItemIcon>
                        <TerminalRoundedIcon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText primary={step} />
                    </ListItem>
                  ))}
                </List>
                {resource.snippet ? (
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: (theme) => theme.palette.action.hover,
                      borderRadius: 1,
                      p: 2,
                      mt: "auto",
                      overflowX: "auto",
                      fontFamily: "JetBrains Mono, Fira Code, SFMono-Regular, Consolas, monospace",
                      fontSize: 13
                    }}
                  >
                    {resource.snippet}
                  </Box>
                ) : null}
                {resource.snippet ? <CopySnippetButton text={resource.snippet} /> : null}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}
